/**
 * register_controller.js — account creation form behaviour (Stimulus).
 *
 * Scoped to the wrapper around BOTH sign-up forms (Google/Xero + email), which
 * share the terms-acceptance state and the heard-from answer. Handles: show/hide
 * password, terms checkbox validation (mirrored into the Google form's hidden
 * field), the heard-from answer copied into the Google/Xero form on submit,
 * submit guards, and the submit spinner. Replaces end-of-body inline <script> that used
 * getElementById + addEventListener + DOMContentLoaded (none survive a Turbo swap).
 *
 * Both forms keep data-turbo="false": email sign-up 302s into the authenticated
 * shell and the Google form redirects to an external OAuth origin, so submit is a
 * full navigation by design. connect() also re-enables the submit button (covers
 * the server re-render of a partially-filled form).
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            var btn = this._q('#registerBtn');
            var spinner = this._q('#registerSpinner');
            if (btn && btn.disabled) {
                btn.disabled = false;
                if (spinner) spinner.classList.add('d-none');
                btn.innerHTML = 'Create account';
            }
        }

        togglePassword(event) {
            var input = this._q('#register_password');
            var icon = this._q('#register_toggle_password_icon');
            var button = event.currentTarget;
            if (!input) return;

            if (input.type === 'password') {
                input.type = 'text';
                if (icon) icon.className = 'bi bi-eye-slash';
                if (button) button.title = 'Hide password';
            } else {
                input.type = 'password';
                if (icon) icon.className = 'bi bi-eye';
                if (button) button.title = 'Show password';
            }
        }

        onTermsChange() {
            var termsBox = this._q('#terms');
            var termsHidden = this._q('#terms_google_hidden');
            var termsFeedback = this._q('#termsFeedback');
            if (!termsBox) return;

            if (termsBox.checked) {
                termsBox.classList.remove('is-invalid');
                if (termsFeedback) termsFeedback.style.display = '';
            }
            if (termsHidden) termsHidden.value = termsBox.checked ? '1' : '';
        }

        onGoogleSubmit(event) {
            var termsBox = this._q('#terms');
            var termsHidden = this._q('#terms_google_hidden');
            if (termsBox && !termsBox.checked) {
                event.preventDefault();
                this._flagTermsMissing();
                return;
            }
            if (termsHidden) termsHidden.value = '1';
            this._carryHeardFrom('#heard_from_google_hidden');
        }

        onXeroSubmit(event) {
            var termsBox = this._q('#terms');
            var termsHidden = this._q('#terms_xero_hidden');
            if (termsBox && !termsBox.checked) {
                event.preventDefault();
                this._flagTermsMissing();
                return;
            }
            if (termsHidden) termsHidden.value = '1';
            this._carryHeardFrom('#heard_from_xero_hidden');
        }

        // The "How did you hear about Chippie?" select sits in the email form,
        // below the Google and Xero buttons; their own forms only submit what
        // is copied into them here.
        _carryHeardFrom(hiddenSelector) {
            var select = this._q('#heard_from');
            var hidden = this._q(hiddenSelector);
            if (select && hidden) hidden.value = select.value;
        }

        onRegisterSubmit(event) {
            var passwordInput = this._q('#register_password');
            var password = passwordInput ? passwordInput.value : '';
            var termsBox = this._q('#terms');

            if (password.length < 8) {
                if (window.showNotification) window.showNotification('Password must be at least 8 characters long', 'error');
                event.preventDefault();
                return;
            }

            var confirmHidden = this._q('#confirm_password_hidden');
            if (confirmHidden) confirmHidden.value = password;

            if (termsBox && !termsBox.checked) {
                event.preventDefault();
                this._flagTermsMissing();
                return;
            }

            var btn = this._q('#registerBtn');
            var spinner = this._q('#registerSpinner');
            if (btn) {
                btn.disabled = true;
                if (spinner) spinner.classList.remove('d-none');
                btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Creating account...';
            }
        }

        _flagTermsMissing() {
            var termsBox = this._q('#terms');
            var termsFeedback = this._q('#termsFeedback');
            if (!termsBox) return;
            termsBox.classList.add('is-invalid');
            if (termsFeedback) termsFeedback.style.display = 'block';
            termsBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Defer focus until scroll settles so iOS Safari doesn't fight it.
            setTimeout(function () { termsBox.focus({ preventScroll: true }); }, 250);
        }

        _q(selector) { return this.element.querySelector(selector); }
}
